import { Box, Typography } from "@outschool/backpack";
import { EnrollmentMeetingPurchaseDuration_ActivityFragment } from "@outschool/gql-frontend-generated";
import { falTimes } from "@outschool/icons";
import { TFunction, useTranslation } from "@outschool/localization";
import {
  fullDurationStringWithStartTime,
  weeklyDayOfWeekWithDuration
} from "@outschool/time";
import {
  IconButton,
  LoadingModal,
  ModalActionButtons
} from "@outschool/ui-legacy-component-library";
import React from "react";

import recurringImage from "../../../images/calendar-recurring.png";
import { useTimeZone } from "../../providers/TimeZoneProvider";
import { EnrollmentDurationSelect } from "./enrollmentForm/EnrollmentDurationSelect";
import RefundPolicy from "./enrollmentForm/RefundPolicy";
import {
  DurationCard,
  useFutureMeetings
} from "./EnrollmentMeetingPurchaseDuration";
import { FutureMeetingInfo } from "./types";

export type EnrollmentMeetingPurchaseDurationProps = {
  startTime: Date;
  endTime: Date;
  activity: EnrollmentMeetingPurchaseDuration_ActivityFragment;
  onBack(e: React.SyntheticEvent): void;
  onClose(e: React.SyntheticEvent): void;
  onNextClick({
    duration,
    canScheduleContinuously,
    futureMeetings
  }: {
    duration: number;
    canScheduleContinuously?: boolean;
    futureMeetings: FutureMeetingInfo[];
  }): void;
  showCloseButton?: boolean;
  orgTeacherUid?: string | null;
};

export default function EnrollmentMeetingPurchasePrepayDuration({
  startTime,
  endTime,
  activity,
  onBack,
  onClose,
  onNextClick,
  showCloseButton = true,
  orgTeacherUid
}: EnrollmentMeetingPurchaseDurationProps) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\EnrollmentMeetingPurchasePrepayDuration"
  );
  const [enrollmentDuration, setEnrollmentDuration] = React.useState(1);

  const { duration, futureMeetings, futureMeetingsLoading } = useFutureMeetings(
    {
      activityUid: activity.uid,
      startTime,
      endTime,
      enrollmentDuration,
      orgTeacherUid
    }
  );

  if (futureMeetingsLoading) {
    return <LoadingModal />;
  }

  const onNext = (e: MouseEvent) => {
    e.preventDefault();
    onNextClick({
      // @ts-ignore TS(2322): Type 'number | null' is not assignable to type 'nu... Remove this comment to see the full error message
      duration,
      futureMeetings
    });
  };

  return (
    <form method="POST" onSubmit={e => e.preventDefault()}>
      <Box
        flex
        sx={{
          gap: "1em",
          flexDirection: "column",
          alignItems: "stretch",
          justifyContent: "space-between",
          padding: "24px 0"
        }}
      >
        <RecurringCard
          startTime={startTime}
          endTime={endTime}
          enrollmentDuration={enrollmentDuration}
          setEnrollmentDuration={setEnrollmentDuration}
          t={t}
        />
        <RefundPolicy priceCents={activity.price_cents} />
        {showCloseButton && (
          <Box
            sx={{
              position: "absolute",
              top: "8px",
              right: "8px",
              zIndex: 1
            }}
          >
            <IconButton
              icon={falTimes}
              sx={{
                fontSize: "large",
                color: "text",
                "&:hover": {
                  color: "#380596"
                }
              }}
              iconProps={{ sx: { fontSize: "1.333em" } }}
              onClick={onClose}
              aria-label={t("Close")}
            />
          </Box>
        )}
      </Box>
      <ModalActionButtons
        cancelLabel={t("Back")}
        onCancel={onBack}
        primaryLabel={t("Next")}
        immediatePrimaryAction={onNext}
        trackingName="tutoring_weeks_selected"
        trackingSharedProperties={{
          activityUid: activity.uid
        }}
      />
    </form>
  );
}

const RecurringCard = ({
  startTime,
  endTime,
  enrollmentDuration,
  setEnrollmentDuration,
  t
}: {
  startTime: Date;
  endTime: Date;
  enrollmentDuration: number;
  setEnrollmentDuration: (val: number) => void;
  t: TFunction;
}) => {
  const { timeZone } = useTimeZone();

  const durationString =
    enrollmentDuration === 1
      ? t("Meet {{meetingDayAndTime}}", {
          meetingDayAndTime: fullDurationStringWithStartTime(
            startTime,
            endTime,
            timeZone
          )
        })
      : t("Save the weekly spot! Meet every {{meetingDayAndTime}}", {
          meetingDayAndTime: weeklyDayOfWeekWithDuration(
            startTime,
            endTime,
            timeZone
          )
        });

  return (
    <DurationCard image={recurringImage} selected={true} selectable={false}>
      <Box flex sx={{ flexDirection: "row", alignItems: "center" }}>
        <Typography
          emphasized
          sx={{ marginRight: ".5em" }}
        >{t`Meeting Frequency`}</Typography>
      </Box>
      {durationString}
      <EnrollmentDurationSelect
        enrollmentDuration={enrollmentDuration}
        setEnrollmentDuration={setEnrollmentDuration}
        isPrepayEnrollment={true}
      />
    </DurationCard>
  );
};
